import type { Guia } from '../tipos';

export const guiaExames: Guia = {
  id: 'exames',
  titulo: 'Testes e exames do pré-natal',
  resumo:
    'Teste rápido, VDRL e outros exames da gestante e do parceiro, e o que o sistema faz com o resultado.',
  perfis: ['enfermeiro', 'medico'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'teste-rapido',
      titulo: 'Registrar um teste rápido',
      passos: [
        'Na ficha da gestante, abra a aba **Exames** e clique em **Teste rápido**.',
        'Escolha o teste (sífilis, HIV, hepatites), a data e o resultado e clique em **Registrar teste**.',
      ],
      observacao:
        'Teste de sífilis ou de HIV **reagente** cria sozinho a notificação SINAN pendente (aba Notificações).',
      botoes: ['Teste rápido', 'Registrar teste'],
      imagem: {
        arquivo: 'prenatal-exames.png',
        descricao:
          'Aba Exames com os botões Teste rápido e VDRL / outro exame e a lista de exames.',
      },
    },
    {
      id: 'laboratorio',
      titulo: 'Registrar VDRL ou outro exame',
      passos: [
        'Na aba **Exames**, clique em **VDRL / outro exame**.',
        'Escolha o exame, a data da coleta e o resultado. No VDRL ou RPR reagente, informe o **Título** (ex.: 1:8).',
        'Clique em **Registrar exame**.',
      ],
      observacao:
        'A série de VDRL/RPR aparece em ordem de data: é com ela que o sistema acompanha o controle de cura.',
      botoes: ['VDRL / outro exame', 'Registrar exame'],
    },
    {
      id: 'corrigir',
      titulo: 'Corrigir um exame',
      passos: [
        'Quem registrou pode editar o próprio registro.',
        'Outro profissional usa **Novo** e explica o motivo: o registro anterior fica guardado, com o novo no lugar.',
      ],
    },
    {
      id: 'parceiro',
      titulo: 'Exames do parceiro',
      passos: [
        'Abra a aba **Parceiro** da ficha da gestante: os testes dele ficam ali, separados.',
        'Teste de sífilis reagente do parceiro cria a notificação de sífilis adquirida.',
      ],
    },
  ],
};

export const guiaTratamento: Guia = {
  id: 'tratamento',
  titulo: 'Tratamento e doses',
  resumo:
    'Iniciar o tratamento de sífilis, registrar doses, reiniciar quando preciso, controle de cura e tratamento do parceiro.',
  perfis: ['enfermeiro', 'medico'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'iniciar',
      titulo: 'Iniciar o tratamento',
      passos: [
        'Na ficha da gestante, abra a aba **Tratamento**.',
        'Escolha o **Estágio clínico**: ele define o número de doses (o sistema mostra a regra do protocolo).',
        'Clique em **Iniciar tratamento**.',
      ],
      botoes: ['Iniciar tratamento'],
    },
    {
      id: 'dose',
      titulo: 'Registrar a dose aplicada',
      passos: [
        'Clique em **Registrar dose aplicada**, confira a data e informe o **Lote**.',
        'O sistema mostra a situação: em dia, com a data da próxima dose, ou dose pendente.',
      ],
      observacao:
        'Para registrar dose, o seu registro profissional (COREN ou CRM) precisa estar cadastrado. Se não estiver, peça ao administrador.',
      botoes: ['Registrar dose aplicada'],
      imagem: {
        arquivo: 'prenatal-tratamento.png',
        descricao:
          'Aba Tratamento com a situação do tratamento, as doses aplicadas e o botão Registrar dose aplicada.',
      },
    },
    {
      id: 'reiniciar',
      titulo: 'Dose fora do prazo: reiniciar',
      passos: [
        'Se o intervalo entre as doses passar do limite do protocolo, o sistema avisa que o tratamento precisa ser reiniciado.',
        'Clique em **Reiniciar tratamento**, informe a data e a justificativa e confirme.',
        'Para gestante faltosa, use **Registrar busca ativa** e anote o que foi feito.',
      ],
      botoes: ['Reiniciar tratamento', 'Registrar busca ativa'],
    },
    {
      id: 'controle-cura',
      titulo: 'Controle de cura',
      passos: [
        'Depois do tratamento completo, registre um VDRL ou RPR por mês até o parto (aba **Exames**).',
        'Se o título subir 2 diluições ou mais em relação ao menor título depois do tratamento, o sistema alerta.',
      ],
    },
    {
      id: 'parceiro',
      titulo: 'Tratamento do parceiro',
      passos: [
        'Na aba **Parceiro**, inicie o tratamento dele do mesmo jeito.',
        'Com teste não reagente, o sistema sugere o **Tratamento presuntivo (dose única)**, como no protocolo.',
        'Se o parceiro não for tratado, use **Registrar motivo**: o motivo vai para a ficha SINAN.',
      ],
      botoes: ['Registrar motivo'],
    },
    {
      id: 'painel',
      titulo: 'Painel do pré-natal',
      passos: [
        'No menu, abra **Pré-natal**: aparecem as gestantes da unidade e o que precisa de atenção (testar, aplicar dose, tratar o parceiro).',
        'Clique numa pendência para abrir a ficha na aba certa.',
      ],
      imagem: {
        arquivo: 'prenatal-painel.png',
        descricao: 'Painel do pré-natal com a lista de gestantes e as pendências de cada uma.',
      },
    },
  ],
};
